import {
  store as blockEditorStore,
  __experimentalBlockVariationPicker as BlockVariationPicker,
} from '@wordpress/block-editor';
import { createBlocksFromInnerBlocksTemplate } from '@wordpress/blocks';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Visibility from '@components/visibility';
import Label from '@components/block-label';
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import variations from './variations';
import { cardIcon } from './icons';
import LinkControls from '@components/link';
import { DEFAULT_CSS_CLASS } from '@constants';
import classnames from 'classnames';
import './editor.scss';

const {
  i18n: { __ },
  blockEditor: {
    useBlockProps,
    useInnerBlocksProps,
    InspectorControls,
    useSetting,
  },
  element: { useEffect },
  components: { ToggleControl, PanelBody },
  data: { useDispatch, withSelect },
} = wp;

const ALLOWED_BLOCKS = [
  'core/paragraph',
  'core/heading',
  'core/image',
  'core/list',
  'b3rg/div',
  'b3rg/icon-block',
  'b3rg/button',
  'b3rg/image-container',
];

const Placeholder = ({ clientId, setAttributes }) => {
  const { replaceInnerBlocks } = useDispatch(blockEditorStore);
  // when skip, first variant will be set as a default
  const defaultVariation = variations[0];
  return (
    <BlockVariationPicker
      variations={variations}
      label={__('Card', 'b3rg')}
      instructions={__('Choose a variation to start with.', 'b3rg')}
      icon={cardIcon}
      onSelect={(variation = defaultVariation) => {
        if (variation.attributes) {
          setAttributes(variation.attributes);
        }
        if (variation.innerBlocks) {
          replaceInnerBlocks(
            clientId,
            createBlocksFromInnerBlocksTemplate(variation.innerBlocks),
            true,
          );
        }
      }}
      allowSkip
    />
  );
};

const EditContainer = ({ name, attributes, setAttributes, context }) => {
  const { blockVisibility, blockClassNames, label, enableLink } = attributes;
  const disableNestedLink = context['b3rg/disableNestedLink'];
  const classNames = getBlockClassNames(blockClassNames);
  const defaultBlockCssClass = DEFAULT_CSS_CLASS.CARD;
  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}`]: true,
      [`${classNames}`]: classNames,
      disable: !blockVisibility,
    }),
  });
  const innerBlocksProps = useInnerBlocksProps(blockProps, {
    // Checks if the allowed blocks array is defined from theme.json
    allowedBlocks:
      useSetting(`custom.blocks.${name}.allowedBlocks`) ?? ALLOWED_BLOCKS,
  });

  useEffect(() => {
    if (typeof disableNestedLink !== 'undefined') {
      setAttributes({ enableLink: disableNestedLink });
    }
  }, [setAttributes, disableNestedLink]);

  return (
    <>
      <InspectorControls>
        <InspectorTabs>
          <InspectorControlsAppearance>
            <ClassDropdown
              {...{
                blockClassNames,
                setAttributes,
                blockClassPrefix: DEFAULT_CSS_CLASS.CARD,
              }}
            />
          </InspectorControlsAppearance>
          <InspectorControlsGeneral>
            <Label {...{ label, setAttributes }} />
            <Visibility {...{ blockVisibility, setAttributes }} />
            {!disableNestedLink && (
              <>
                <PanelBody>
                  <ToggleControl
                    label={__('Wrap with Link', 'b3rg')}
                    checked={enableLink}
                    onChange={(enableLink) => {
                      setAttributes({ enableLink });
                    }}
                  />
                </PanelBody>
                {enableLink && (
                  <LinkControls {...{ attributes, setAttributes }} />
                )}
              </>
            )}
          </InspectorControlsGeneral>
        </InspectorTabs>
      </InspectorControls>
      <div {...innerBlocksProps} />
    </>
  );
};

const Edit = (props) => {
  const { blocks } = props;
  const Component = blocks.length > 0 ? EditContainer : Placeholder;

  return <Component {...props} />;
};

export default withSelect((select, { clientId }) => {
  const { getBlocks } = select('core/block-editor');
  const blocks = getBlocks(clientId);
  return { blocks };
})(Edit);
